import { useRef, useState } from "react";
import { TopBar } from "../components/TopBar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { api, API } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Save, Upload, Trash2, Image as ImageIcon, Building2, Boxes, Loader2, Clock, Eye, EyeOff, ShieldCheck, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function SettingsPage() {
  const settings = useSettings();
  const [supplierName, setSupplierName] = useState(settings.supplier_name || "Ultra");
  const [savingName, setSavingName] = useState(false);
  const [overdueDays, setOverdueDays] = useState(String(settings.overdue_days || 7));
  const [overdueMode, setOverdueMode] = useState(settings.overdue_mode || "days_with_third_party");
  const [savingOverdue, setSavingOverdue] = useState(false);
  const [togglingDemo, setTogglingDemo] = useState(false);
  const demoOn = settings.show_demo_logins !== false;

  const toggleDemo = async () => {
    setTogglingDemo(true);
    try {
      await api.patch("/settings", { show_demo_logins: !demoOn });
      await settings.reload();
      toast.success(!demoOn ? "Demo accounts are now visible on /login" : "Demo accounts hidden on /login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setTogglingDemo(false);
    }
  };

  const saveSupplier = async (e) => {
    e?.preventDefault?.();
    setSavingName(true);
    try {
      await api.patch("/settings", { supplier_name: supplierName });
      await settings.reload();
      toast.success("Supplier name updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingName(false);
    }
  };

  const saveOverdue = async (e) => {
    e?.preventDefault?.();
    const n = parseInt(overdueDays, 10);
    if (Number.isNaN(n) || n < 1 || n > 90) {
      toast.error("Pick a number between 1 and 90");
      return;
    }
    setSavingOverdue(true);
    try {
      await api.patch("/settings", { overdue_days: n, overdue_mode: overdueMode });
      await settings.reload();
      toast.success("Overdue rules updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingOverdue(false);
    }
  };

  const overdueDirty =
    String(settings.overdue_days || 7) !== overdueDays ||
    (settings.overdue_mode || "days_with_third_party") !== overdueMode;
  const showDaysInput = overdueMode !== "past_expected_return";

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-6">
        <div className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950">
            Settings
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Branding and labels used across the platform. Changes apply immediately for everyone.
          </p>
        </div>

        {/* Roles & Permissions link */}
        <Section title="Roles & Permissions" icon={ShieldCheck}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-xs text-zinc-500 leading-relaxed max-w-md">
              Create custom roles and pick exactly what each one can do — from viewing records to
              posting comments, deleting records or managing users. System roles are pre-configured
              and can be fine-tuned but not deleted.
            </p>
            <Link
              to="/settings/roles"
              className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-md px-3 py-1.5 transition-colors"
              data-testid="open-roles-manager"
            >
              Manage roles <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Section>

        {/* Supplier name */}
        <Section title="Plain bath supplier name" icon={Boxes}>          <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
            This name shows wherever the platform refers to the supplier of the plain bath (column headers, field labels, create-record dialog). For example: <span className="font-mono">{settings.supplier_name || "Ultra"}</span>.
          </p>
          <form onSubmit={saveSupplier} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Name</Label>
              <Input
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Ultra"
                className="mt-1.5"
                data-testid="supplier-name-input"
                required
              />
            </div>
            <Button type="submit" disabled={savingName || !supplierName.trim() || supplierName === settings.supplier_name} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="supplier-name-save">
              <Save className="h-3.5 w-3.5 mr-1.5" />
              {savingName ? "Saving…" : "Save"}
            </Button>
          </form>
        </Section>

        {/* Overdue threshold */}
        <Section title="Overdue rules" icon={Clock}>
          <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
            Choose how a bath becomes <span className="font-semibold text-amber-700">overdue</span>. Overdue records get an amber accent on the dashboard and feed the Overdue KPI.
          </p>
          <form onSubmit={saveOverdue} className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-zinc-500">When to flag overdue</Label>
              <Select value={overdueMode} onValueChange={setOverdueMode}>
                <SelectTrigger className="mt-1.5" data-testid="overdue-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days_with_third_party">
                    More than N days with the third party
                  </SelectItem>
                  <SelectItem value="past_expected_return">
                    Past the Expected Back date
                  </SelectItem>
                  <SelectItem value="either">
                    Either of the above (whichever happens first)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                {overdueMode === "days_with_third_party" &&
                  "Counts days from the Collected date. Good when you don't always set an expected return."}
                {overdueMode === "past_expected_return" &&
                  "Flags overdue on the day after Expected Back to WeLove. Skipped if no expected date is set."}
                {overdueMode === "either" &&
                  "Flags overdue if any record passes the day limit OR is past the expected back date."}
              </p>
            </div>

            {showDaysInput && (
              <div className="max-w-[200px]">
                <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Days with third party</Label>
                <Input
                  type="number"
                  min="1"
                  max="90"
                  value={overdueDays}
                  onChange={(e) => setOverdueDays(e.target.value)}
                  className="mt-1.5"
                  data-testid="overdue-days-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={savingOverdue || !overdueDirty}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="overdue-days-save"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              {savingOverdue ? "Saving…" : "Save"}
            </Button>
          </form>
        </Section>

        {/* Demo logins visibility */}
        <Section title="Demo logins" icon={demoOn ? Eye : EyeOff}>
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-[220px]">
              <p className="text-xs text-zinc-500 leading-relaxed">
                When ON, the login screen at <span className="font-mono">/login</span> shows clickable demo-account chips (admin / warehouse / office / Giavani / read-only) — handy for product demos.
                <br />
                When OFF, the chips disappear and only the username/password form remains — best for live operations.
              </p>
            </div>
            <div className="flex items-center gap-3 self-center">
              <span className={`text-xs font-semibold ${demoOn ? "text-zinc-900" : "text-zinc-400"}`}>
                {demoOn ? "Visible" : "Hidden"}
              </span>
              <Switch
                checked={demoOn}
                onCheckedChange={toggleDemo}
                disabled={togglingDemo}
                data-testid="demo-logins-toggle"
              />
            </div>
          </div>
        </Section>
        <LogoSection
          title="WeLove logo"
          subtitle="Shown on the customer tracking page (the page your customers see)."
          logoKey="welove"
          currentUrl={settings.welove_logo_url}
          onChange={() => settings.reload()}
        />

        {/* Giavani logo */}
        <LogoSection
          title="Giavani logo"
          subtitle="Shown on the third-party 'My Assignments' page when Giavani users log in."
          logoKey="giavani"
          currentUrl={settings.giavani_logo_url}
          onChange={() => settings.reload()}
        />

        <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed flex items-start gap-1.5">
          <Building2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          The platform brand (<span className="font-semibold">Flowline · by Soubor</span>) stays consistent everywhere — only the merchant-facing and third-party-facing visuals change here.
        </p>
      </main>
    </div>
  );
}

function LogoSection({ title, subtitle, logoKey, currentUrl, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use a JPEG, PNG or WebP image");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/settings/logo/${logoKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${title} updated`);
      setCacheBust((n) => n + 1);
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${title}? The default Flowline icon will be used instead.`)) return;
    try {
      await api.delete(`/settings/logo/${logoKey}`);
      toast.success(`${title} removed`);
      setCacheBust((n) => n + 1);
      onChange && onChange();
    } catch {
      toast.error("Remove failed");
    }
  };

  return (
    <Section title={title} icon={ImageIcon}>
      <p className="text-xs text-zinc-500 mb-3 leading-relaxed">{subtitle}</p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="h-20 w-20 rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden">
          {currentUrl ? (
            <img
              key={cacheBust}
              src={`${currentUrl}?v=${cacheBust}`}
              alt={title}
              className="max-h-full max-w-full object-contain"
              data-testid={`logo-preview-${logoKey}`}
            />
          ) : (
            <ImageIcon className="h-6 w-6 text-zinc-300" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onFile}
            data-testid={`logo-input-${logoKey}`}
          />
          <Button type="button" size="sm" onClick={onPick} disabled={uploading} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid={`logo-upload-${logoKey}`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
            {uploading ? "Uploading…" : currentUrl ? "Replace" : "Upload logo"}
          </Button>
          {currentUrl && (
            <Button type="button" size="sm" variant="outline" onClick={remove} className="border-zinc-200 text-rose-600 hover:bg-rose-50" data-testid={`logo-remove-${logoKey}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
            </Button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-zinc-400 mt-3">PNG/JPEG/WebP · max 6MB · square shape works best.</p>
    </Section>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-zinc-200 rounded-md shadow-sm mb-4">
    <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
      <h3 className="font-heading font-bold text-sm tracking-tight text-zinc-950">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);
